import { useIntl } from 'react-intl'

import { DistanceToProviderValue } from '../../../models/providers/Providers'
import {
  IN_PERSON_WITH_DISTANCE_BADGE_TEXT,
  LIVE_MESSAGING_BADGE_TEXT,
  ONSITE_BADGE_TEXT,
  VIDEO_BADGE_TEXT,
} from '../constants'

/**
 * Hook that returns an array of formatted meeting option labels based on provider modalities.
 *
 * Determines which meeting types are available for a provider and returns localized
 * display strings for each eligible option:
 * - Onsite meetings (when both onsite and in-person modalities are available)
 * - In-person meetings with distance (when only in-person is available and distance is provided)
 * - Video meetings
 * - Live messaging
 *
 * @param hasOnsiteModality - Whether the provider supports onsite meetings
 * @param hasInPersonModality - Whether the provider supports in-person meetings
 * @param distanceToProvider - Distance information including value and unit
 * @param hasVideoModality - Whether the provider supports video meetings
 * @param hasLiveMessagingModality - Whether the provider supports live messaging
 *
 * @returns Array of localized meeting type strings for display
 */
export const useGetProviderMeetingOptions = ({
  hasOnsiteModality,
  hasInPersonModality,
  distanceToProvider,
  hasVideoModality,
  hasLiveMessagingModality,
}: {
  hasOnsiteModality?: boolean
  hasInPersonModality?: boolean
  distanceToProvider?: DistanceToProviderValue
  hasVideoModality?: boolean
  hasLiveMessagingModality?: boolean
}) => {
  const { formatMessage } = useIntl()

  const eligibleMeetingTypes = []
  if (hasVideoModality) {
    eligibleMeetingTypes.push(formatMessage(VIDEO_BADGE_TEXT))
  }

  if (hasOnsiteModality && hasInPersonModality) {
    eligibleMeetingTypes.push(formatMessage(ONSITE_BADGE_TEXT))
  }
  if (!hasOnsiteModality && hasInPersonModality && distanceToProvider) {
    eligibleMeetingTypes.push(
      formatMessage(IN_PERSON_WITH_DISTANCE_BADGE_TEXT, {
        distance: distanceToProvider.value,
        distanceUnit: distanceToProvider.unit,
      }),
    )
  }
  if (hasLiveMessagingModality) {
    eligibleMeetingTypes.push(formatMessage(LIVE_MESSAGING_BADGE_TEXT))
  }
  return eligibleMeetingTypes
}
